/**
 * Whether a store is ready to sell, and how well set up it is.
 *
 * Two lists, scored differently on purpose.
 *
 * **Required** is all-or-nothing and deliberately unweighted. A store cannot be
 * "90% allowed to sell" — either the things an order needs in order to be
 * fulfilled and invoiced exist, or they do not. Weighting a required list would
 * produce a number that looks like progress and means nothing.
 *
 * **Recommended** is weighted, because those items genuinely differ in value: a
 * theme changes what every visitor sees, a knowledge-base article does not.
 *
 * The percentage is a *summary of the checklist*, never a replacement for it.
 * "You are 60% ready" is unactionable; "you have no categories, and here is the
 * link" is the thing a merchant can act on. So every item carries a route.
 */

export type ReadinessGroup = 'required' | 'recommended';

export interface ReadinessItem {
  key: string;
  group: ReadinessGroup;
  label: string;
  /** Why it matters, in the merchant's terms — not a restatement of the label. */
  why: string;
  /**
   * Where to go and fix it.
   *
   * Must be a real admin route — a checklist that links into a 404 is worse
   * than one with no links, because it spends the merchant's trust before it
   * spends their time. Verified against admin/src/app/app.routes.ts.
   */
  route: string;
  done: boolean;
  /** Recommended items only. Required items are never weighted — see above. */
  weight?: number;
  /**
   * True when the platform set this up rather than the merchant.
   *
   * Worth surfacing: a merchant who sees "Shipping ✓" without having touched it
   * should know a default is in force, or they will discover their own pricing
   * only when an order arrives.
   */
  seeded?: boolean;
}

export interface ReadinessReport {
  items: ReadinessItem[];
  /** Every required item done. The gate on creating a product. */
  canSell: boolean;
  requiredDone: number;
  requiredTotal: number;
  /** Weighted, 0–100. */
  recommendedPercent: number;
  /** What is left, most valuable first. */
  missing: ReadinessItem[];
}

/**
 * Where the meter stops asking.
 *
 * Not a hiding place for the last few items — see {@link readinessDisplay}. A
 * threshold only means anything if something happens at it, and what happens
 * here is that the meter stops being *sticky*: the remaining items are genuinely
 * optional, so continuing to follow a merchant around every screen would be a
 * nag they learn to ignore rather than a prompt they act on.
 */
export const RECOMMENDED_SETTLED_PERCENT = 80;

export function scoreReadiness(items: ReadinessItem[]): ReadinessReport {
  const required = items.filter(i => i.group === 'required');
  const recommended = items.filter(i => i.group === 'recommended');

  const requiredDone = required.filter(i => i.done).length;

  const weightTotal = recommended.reduce((sum, i) => sum + (i.weight ?? 1), 0);
  const weightDone = recommended
    .filter(i => i.done)
    .reduce((sum, i) => sum + (i.weight ?? 1), 0);

  const missing = items
    .filter(i => !i.done)
    // Required first, then the heaviest recommendation — the order a merchant
    // should work through them in.
    .sort((a, b) => {
      if (a.group !== b.group) return a.group === 'required' ? -1 : 1;
      return (b.weight ?? 1) - (a.weight ?? 1);
    });

  return {
    items,
    canSell: requiredDone === required.length,
    requiredDone,
    requiredTotal: required.length,
    recommendedPercent: weightTotal ? Math.round((weightDone / weightTotal) * 100) : 100,
    missing,
  };
}

export type ReadinessTone = 'blocking' | 'nudge' | 'settled';

/**
 * How prominent the meter should be.
 *
 * The design decision this encodes: prominence *decays*, it does not switch off.
 *
 *   - `blocking` — required work outstanding. A full banner, because the
 *     merchant cannot sell and needs to know why now.
 *   - `nudge`    — able to sell, recommendations outstanding. A small sticky
 *     pill. Present, ignorable, not in the way.
 *   - `settled`  — past the threshold. Nothing sticky; the checklist is still
 *     there for anyone who goes looking.
 *
 * A banner that never goes away is a banner merchants stop reading, which costs
 * the platform the one moment it genuinely needed their attention.
 */
export function readinessDisplay(report: ReadinessReport): ReadinessTone {
  if (!report.canSell) return 'blocking';
  return report.recommendedPercent >= RECOMMENDED_SETTLED_PERCENT ? 'settled' : 'nudge';
}

/** The facts a store's readiness is computed from. */
export interface ReadinessFacts {
  hasCity: boolean;
  hasState: boolean;
  hasGstin: boolean;
  categoryCount: number;
  taxProfileCount: number;
  hasStorage: boolean;
  hasShippingChannel: boolean;
  shippingSeeded: boolean;

  hasLogo: boolean;
  seoPercent: number;
  hasThemeChosen: boolean;
  hasTemplateChosen: boolean;
  faqCount: number;
  discountCount: number;
  hasEmailSettings: boolean;
  hasOpeningHours: boolean;
}

/**
 * The checklist.
 *
 * What is required is decided by one question: **can an order be fulfilled and
 * invoiced without it?** Everything else is a recommendation, however much it
 * improves the shop. That test is what keeps the required list short enough to
 * be worth blocking on — a long required list is a sign-up flow merchants
 * abandon rather than a quality bar they clear.
 */
export function buildReadiness(facts: ReadinessFacts): ReadinessReport {
  const items: ReadinessItem[] = [
    // ── Required ────────────────────────────────────────────────────────
    {
      key: 'address', group: 'required', label: 'Shop address',
      why: 'Decides your courier zone and whether GST is charged as CGST+SGST or IGST.',
      route: '/merchant/stores',
      done: facts.hasCity && facts.hasState,
    },
    {
      key: 'gstin', group: 'required', label: 'GST number',
      why: 'Online sellers must register for GST regardless of turnover, and it appears on every invoice.',
      route: '/settings/tax',
      done: facts.hasGstin,
    },
    {
      key: 'categories', group: 'required', label: 'At least one category',
      why: 'Every product belongs to one, so a product cannot be created without it.',
      route: '/catalog/categories',
      done: facts.categoryCount > 0,
    },
    {
      key: 'tax', group: 'required', label: 'A tax slab',
      why: 'Products need a GST rate before they can be sold or invoiced.',
      route: '/settings/tax',
      done: facts.taxProfileCount > 0,
      seeded: facts.taxProfileCount > 0,
    },
    {
      key: 'storage', group: 'required', label: 'Image storage',
      why: 'Product photographs need somewhere to live.',
      route: '/settings/storage',
      done: facts.hasStorage,
      seeded: facts.hasStorage,
    },
    {
      key: 'shipping', group: 'required', label: 'Delivery pricing',
      why: 'An order cannot be priced without it.',
      route: '/settings/shipping',
      done: facts.hasShippingChannel,
      seeded: facts.shippingSeeded,
    },

    // ── Recommended ─────────────────────────────────────────────────────
    {
      key: 'logo', group: 'recommended', weight: 3, label: 'Shop logo',
      why: 'Appears in your storefront header, on invoices and in every email.',
      route: '/merchant/stores',
      done: facts.hasLogo,
    },
    {
      key: 'seo', group: 'recommended', weight: 3, label: 'Search listing',
      why: 'Title, description and address are what put your shop in local results.',
      route: '/merchant/stores',
      done: facts.seoPercent >= 60,
    },
    {
      key: 'theme', group: 'recommended', weight: 2, label: 'Theme',
      why: 'Your colours and fonts. Until it is set, your shop looks like everyone else’s.',
      route: '/merchant/theme',
      done: facts.hasThemeChosen,
    },
    {
      key: 'email', group: 'recommended', weight: 2, label: 'Email sender',
      why: 'Order confirmations go out under your name rather than a default.',
      route: '/settings/templates',
      done: facts.hasEmailSettings,
    },
    {
      key: 'hours', group: 'recommended', weight: 2, label: 'Opening hours',
      why: 'Answers the most common question a local shop is asked, in search and on the page.',
      route: '/merchant/stores',
      done: facts.hasOpeningHours,
    },
    {
      key: 'faq', group: 'recommended', weight: 2, label: 'Questions and answers',
      why: 'What ChatGPT, Gemini and Perplexity quote when someone asks about your shop.',
      route: '/merchant/stores',
      done: facts.faqCount > 0,
    },
    {
      key: 'template', group: 'recommended', weight: 1, label: 'Storefront layout',
      why: 'A layout you picked, rather than the default one.',
      route: '/merchant/storefront-template',
      done: facts.hasTemplateChosen,
    },
    {
      key: 'discounts', group: 'recommended', weight: 1, label: 'A discount code',
      why: 'Something to offer first-time customers.',
      route: '/marketing/discounts',
      done: facts.discountCount > 0,
    },
  ];

  return scoreReadiness(items);
}
